import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeUp } from "./Reveal";
import { EmSwash } from "./EmSwash";
import { TrustCard } from "./TrustCard";
import { SocialLinks } from "./SocialLinks";
import { DonateComingSoonDialog } from "./DonateComingSoonDialog";
import { DoodleDivider } from "./DoodleDivider";
import { IMAGES, FOUNDER } from "../data/content";
import { useNavLinks } from "../lib/useNavLinks";
import { MRA_EIN } from "../lib/donorProfile";
import { openPreferences } from "../lib/consent";


const DONATIONS_LIVE =
  String(process.env.REACT_APP_DONATIONS_LIVE || "").toLowerCase() === "true";
const GIVE_LABEL = DONATIONS_LIVE ? "Give to the General Fund" : "Notify me when giving opens";

/**
 * Footer. The "Join the family" band carries three universal CTAs on every
 * full-variant page (Preview the platform / Give / Contact us), so the old
 * per-page label + handler props are accepted but no longer read. The
 * "Contact us" CTA is dropped on /contact itself.
 */
export const Footer = ({ variant = "full" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const navLinks = useNavLinks();
  const [donateOpen, setDonateOpen] = useState(false);
  const goToSection = (hash) => (e) => {
    // Cross-route hash: e.g., "/demo#campaigns"
    if (hash.startsWith("/")) {
      const [path, anchor] = hash.split("#");
      if (location.pathname !== path) {
        e.preventDefault();
        navigate(hash);
        setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" }), 250);
      } else {
        e.preventDefault();
        if (anchor) document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" });
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    // In-page anchor — target either / or /demo depending on which context we're in.
    const landingPath = location.pathname === "/demo" || location.pathname.startsWith("/demo/")
      ? "/demo"
      : "/";
    if (location.pathname !== landingPath) {
      e.preventDefault();
      navigate(`${landingPath}${hash}`);
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  };

  const isMinimal = variant === "minimal";

  return (
    <footer className="bg-secondary text-ink">
      <div className={`relative mx-auto max-w-7xl overflow-hidden px-6 lg:px-8 ${isMinimal ? "pt-14 lg:pt-20" : "pt-20 lg:pt-32"}`}>
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url(${IMAGES.pattern})`,
            backgroundSize: "540px",
            backgroundRepeat: "repeat",
            WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 20%, rgba(0,0,0,0) 50%)",
            maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 20%, rgba(0,0,0,0) 50%)",
          }}
          aria-hidden="true"
        />
        <div className="relative z-10">
        {/* Upper block — "Join the My Rich Aunt Family" pitch + three universal
            CTAs + family signoff, TrustCard alongside. Skipped on
            `variant="minimal"` (e.g. on the donor demo, where the page
            shouldn't circle donors back into another marketing pitch). */}
        {!isMinimal && (
        <div className="grid grid-cols-1 gap-y-14 lg:grid-cols-12 lg:items-center lg:gap-x-16 lg:gap-y-0">
          {/* Left — one headline, one sentence, the CTAs, and the closing line in the body voice. */}
          <div className="lg:col-span-7">
            <FadeUp>
              <h2 className="max-w-4xl font-serif text-3xl lg:text-[3.5rem] leading-[1.02] tracking-tight">
                Join the <EmSwash color="text-cobalt">My Rich Aunt Family.</EmSwash>
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-olive">
                Every gift to My Rich Aunt is tax-deductible and becomes grants for real, verified students.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="footer-cta-group">
                <button
                  data-testid="footer-give-button"
                  onClick={() => setDonateOpen(true)}
                  className="rounded-full bg-ink px-6 py-4 text-base font-semibold text-sand transition-colors hover:bg-forest"
                >
                  {GIVE_LABEL}
                </button>
                <button
                  data-testid="footer-preview-button"
                  onClick={() => navigate("/preview")}
                  className="group inline-flex items-center gap-1.5 px-2 py-3 text-base font-semibold text-ink transition-colors hover:text-tangerine"
                >
                  Preview the platform
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                </button>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p data-testid="footer-signoff" className="mt-8 max-w-md font-serif text-base italic leading-relaxed text-olive">
                {FOUNDER.footerSignoff}
              </p>
            </FadeUp>
          </div>

          {/* Right — TrustCard, centred on the left block. */}
          <div className="lg:col-span-5 lg:col-start-8" data-testid="footer-trust-slot">
            <FadeUp delay={0.2}>
              <TrustCard />
            </FadeUp>
          </div>
        </div>
        )}

        {/* The page's one palette-dash row: precedes the lower footer (link columns) on every variant. */}
        <DoodleDivider className={`!px-0 ${isMinimal ? "!pt-0" : "mt-12 lg:mt-20"}`} />
        <div className="grid grid-cols-2 gap-8 py-8 pb-16 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            {/* Brand column anchor: sized to the header lockup (h-24/h-28) and
                nudged up so the cap's optical top sits on the eyebrow cap line. */}
            <img src={IMAGES.logoWordmark} alt="My Rich Aunt" className="-mt-1.5 h-24 w-auto lg:h-32" data-testid="footer-logo" />
            <p className="mt-6 max-w-[26ch] font-serif text-xl italic leading-snug text-ink/80 lg:text-2xl">
              Connecting generous donors with verified students who could use a hand.
            </p>
          </div>
          <div>
            <p className="text-[13px] uppercase tracking-[0.2em] text-olive/70">Explore</p>
            <ul className="mt-4 space-y-3 text-[13px]">
              {navLinks.map((l) => (
                <li key={l.href}><a href={l.href} onClick={goToSection(l.href)} className="text-olive transition-colors hover:text-tangerine">{l.label}</a></li>
              ))}
              <li><a href="#manifesto" onClick={goToSection("#manifesto")} className="text-olive transition-colors hover:text-tangerine">Our mission</a></li>
              <li><Link to="/demo/story" data-testid="footer-student-demo-link" className="text-olive transition-colors hover:text-tangerine">Watch the Student Demo</Link></li>
              <li><Link to="/demo/donor-story" data-testid="footer-donor-demo-link" className="text-olive transition-colors hover:text-tangerine">Watch the Donor Demo</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-[13px] uppercase tracking-[0.2em] text-olive/70">Legal</p>
            <ul className="mt-4 space-y-3 text-[13px]">
              <li><Link to="/privacy" className="text-olive transition-colors hover:text-tangerine">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-olive transition-colors hover:text-tangerine">Terms &amp; Conditions</Link></li>
              <li><button type="button" onClick={openPreferences} data-testid="footer-cookie-preferences" className="text-olive transition-colors hover:text-tangerine">Cookie preferences</button></li>
            </ul>
          </div>
          <div>
            <p className="text-[13px] uppercase tracking-[0.2em] text-olive/70">Say hello</p>
            <ul className="mt-4 space-y-3 text-[13px]">
              <li><Link to="/contact" data-testid="footer-contact-link" className="text-olive transition-colors hover:text-tangerine">Send us a message</Link></li>
              <li><a href="mailto:hellofrom@myrichaunt.org" className="text-olive transition-colors hover:text-tangerine">hellofrom@myrichaunt.org</a></li>
            </ul>
            <SocialLinks className="mt-5" size="sm" />
          </div>
        </div>
        </div>
      </div>

      {/* Full-width brand accent band */}
      <div className="bg-yellow text-ink" data-testid="footer-copyright-bar">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-6 py-6 text-[13px] font-medium sm:flex-row sm:items-center sm:justify-between lg:px-8">
          <span>&copy;2022 &ndash; 2026 My Rich Aunt, a 501(c)(3) not-for-profit corporation.</span>
          <span data-testid="footer-ein">EIN {MRA_EIN}</span>
        </div>
      </div>
      {!isMinimal && <DonateComingSoonDialog open={donateOpen} onOpenChange={setDonateOpen} />}
    </footer>
  );
};
